import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BuildSheetViewer } from "@/components/BuildSheetViewer";
import { HelpTip } from "@/components/HelpTip";
import { RefusedUploads } from "@/components/RefusedUploads";
import { Check, FileText, Loader2, Upload, AlertTriangle, Info, Cpu, X } from "lucide-react";

/** ENGINE DATAPLATES — the maker's own printed record, read exactly off their PDF.
 *
 *  His rules on the screen: every plate shown on its own, labelled with the words the maker
 *  printed; every value its own ✓verify; a blank says "Not on this plate" rather than hiding;
 *  a quiet line naming where two plates differ; and a printed VIN that disagrees with this truck
 *  said out loud instead of guessed at. */
export function DataplatePanel({ kind, id, canManage = false, canVerify = false, testid = "dataplate" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [refusedTick, setRefusedTick] = useState(0);
  const fileRef = useRef(null);
  const base = kind === "donor" ? "donors" : "parts";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${base}/${id}/dataplates`);
      setData(data);
    } catch { setData({ plates: [], differences: [], history: [] }); }
  }, [base, id]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/${base}/${id}/dataplates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const bits = [];
      if (data.added) bits.push(`${data.added} dataplate${data.added === 1 ? "" : "s"} read`);
      if (data.already_on_file) bits.push(`${data.already_on_file} already on file`);
      toast.success(`${bits.join(" · ")} — engine ${data.serials?.join(", ")}`);
      (data.vin_warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      if ((data.conflicts || []).length) {
        toast.warning(`${data.conflicts.length} value${data.conflicts.length === 1 ? "" : "s"} on this print disagree with what is already on file. Nothing was changed — both are recorded.`, { duration: 14000 });
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That file could not be read.");
      if (e.response) setRefusedTick((t) => t + 1);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (plate) => {
    if (!window.confirm(`Remove the ${plate.plate_name} plate for engine ${plate.serial}? Use this only when it was loaded against the wrong truck or part — the PDF stays on file.`)) return;
    try {
      await api.delete(`/dataplates/${plate.id}`);
      toast.success(`${plate.plate_name} removed from this record.`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const verify = async (plateId, fieldKey) => {
    try {
      await api.post(`/dataplates/${plateId}/verify`, { field_key: fieldKey });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div data-testid={`${testid}-loading`} className="py-6 text-center font-mono text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;

  const plates = data.plates || [];
  return (
    <div data-testid={`${testid}-panel`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-[11px] text-slate-500 max-w-xl">
          Read straight off the maker's PDF — <b className="text-slate-700">exact, no AI, no cost</b>.
          Save the plate from Cummins (QuickServe, or the new parts.cummins.com page) with <b>Print → Save as PDF</b> (not a screenshot), and print
          each plate an engine has.
          <HelpTip text="An engine can carry more than one plate — the original, and a distributor upfit printed later. Both are kept, side by side, labelled with the words Cummins printed. Neither ever overwrites the other.">
            <button type="button" data-testid={`${testid}-help`} className="ml-1 align-middle text-slate-400 hover:text-slate-700">
              <Info className="w-3.5 h-3.5 inline" />
            </button>
          </HelpTip>
        </p>
        {canManage && (
          <div className="shrink-0">
            <input ref={fileRef} data-testid={`${testid}-upload-input`} type="file" accept="application/pdf,.pdf"
              className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <Button data-testid={`${testid}-upload`} onClick={() => fileRef.current?.click()} disabled={busy}
              variant="outline" className="rounded-lg border-slate-300 h-9">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {plates.length ? "Add another dataplate" : "Add engine dataplate (PDF)"}
            </Button>
          </div>
        )}
      </div>

      <RefusedUploads params={kind === "donor" ? { donor_id: id, where: "truck_dataplate" } : { part_id: id, where: "part_dataplate" }}
        refreshKey={refusedTick} testid={`${testid}-refused`} />

      {(data.waiting_offers || []).map((w) => (
        <WaitingOffer key={w.serial} w={w} kind={kind} id={id} canManage={canManage} onDone={load} testid={testid} />
      ))}

      {plates.length === 0 ? (
        <div data-testid={`${testid}-empty`} className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-4 text-sm text-slate-500 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-slate-400 shrink-0" />
          <span data-testid={`${testid}-empty-note`}>No Cummins dataplate yet</span>
        </div>
      ) : (
        <div className={`grid gap-3 ${plates.length > 1 ? "lg:grid-cols-2" : ""}`}>
          {plates.map((p) => (
            <PlateCard key={p.id} plate={p} canVerify={canVerify} onVerify={verify}
              onSeePdf={() => setPdf(p)} onRemove={canManage ? () => remove(p) : null} testid={testid} />
          ))}
        </div>
      )}

      {(data.differences || []).length > 0 && (
        <div data-testid={`${testid}-differences`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
            <Info className="w-3.5 h-3.5" />These values differ between the plates
          </span>
          <ul className="mt-1 space-y-0.5">
            {data.differences.map((d) => (
              <li key={d.key} data-testid={`${testid}-diff-${d.key}`} className="font-mono text-[11px]">
                <span className="text-slate-500">{d.label}:</span>{" "}
                {d.on.map((o, i) => (
                  <span key={i}>{i > 0 && <span className="text-slate-300"> · </span>}
                    <span className="text-slate-400">{o.plate}</span>{" "}
                    <b className={o.blank ? "text-slate-400 font-normal italic" : "text-slate-800"}>{o.blank ? "not on this plate" : o.value}</b>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.history || []).length > 0 && (
        <div data-testid={`${testid}-history`} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-600">
          {data.history.map((h) => (
            <div key={h.id} data-testid={`${testid}-history-${h.serial}`} className="flex flex-wrap items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              <span>{h.note}</span>
              {h.part_id && (
                <Link to={`/parts/${h.part_id}`} data-testid={`${testid}-history-link-${h.serial}`}
                  className="font-mono text-[11px] text-[#B01E28] hover:underline">{h.part_code || "the engine part"}</Link>
              )}
              <span className="text-slate-400">· the plate now belongs to the engine part</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!pdf} onOpenChange={(o) => !o && setPdf(null)}>
        <DialogContent data-testid={`${testid}-pdf-dialog`} className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-base">{pdf?.plate_name} · ESN {pdf?.serial}</DialogTitle></DialogHeader>
          {pdf && <BuildSheetViewer buildSheet={{ file_id: pdf.file_id, original_name: pdf.original_name, content_type: "application/pdf" }} testid={`${testid}-pdf`} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// A PLATE FROM THE WAITING LIST whose ESN matches this engine — offered, never attached by itself.
function WaitingOffer({ w, kind, id, canManage, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const attach = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dataplates/waiting/${encodeURIComponent(w.serial)}/attach`, { target: kind === "donor" ? "truck" : "part", id });
      toast.success(data.message || "Attached");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testid}-waiting-offer-${w.serial}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-sky-900">
      <Cpu className="w-4 h-4 shrink-0" />
      <span className="flex-1">A waiting dataplate matches this engine — <b className="font-mono">ESN {w.serial}</b> ({w.plates.join(" · ")}).</span>
      {canManage && (
        <Button size="sm" data-testid={`${testid}-waiting-attach-${w.serial}`} disabled={busy} onClick={attach} className="h-8 rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Attach it here
        </Button>
      )}
    </div>
  );
}

function PlateCard({ plate, canVerify, onVerify, onSeePdf, onRemove, testid }) {
  const k = `${plate.serial}-${plate.plate_key}`;
  const rows = [{ key: "esn", label: plate.serial_label, value: plate.serial, blank: false,
                  verified_by: plate.serial_verified_by, verified_at: plate.serial_verified_at },
                ...(plate.fields || [])];
  return (
    <div data-testid={`${testid}-plate-${k}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/70">
        <Badge data-testid={`${testid}-plate-name-${k}`} className="rounded-md bg-slate-800 text-white text-[10px] font-mono normal-case">{plate.plate_name}</Badge>
        <span className="font-mono text-[11px] text-slate-500">ESN {plate.serial}</span>
        <button data-testid={`${testid}-see-pdf-${k}`} onClick={onSeePdf}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E28]">
          <FileText className="w-3.5 h-3.5" />See the plate
        </button>
        {onRemove && (
          <button data-testid={`${testid}-remove-${k}`} onClick={onRemove}
            title="Loaded against the wrong truck or part? Remove it from this record."
            className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#B01E28]">
            <X className="w-3.5 h-3.5" />Wrong record
          </button>
        )}
      </div>
      {plate.vin_mismatch && (
        <div data-testid={`${testid}-vin-warning-${k}`} className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-[#8a5a12]">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Printed against VIN <b className="font-mono">{plate.vin_mismatch.printed}</b>, but this truck is <b className="font-mono">{plate.vin_mismatch.truck}</b>. Nothing was changed — check which is right.</span>
        </div>
      )}
      {(plate.unknown_labels || []).length > 0 && (
        <div data-testid={`${testid}-unknown-${k}`} className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] text-slate-500">
          This print also carries {plate.unknown_labels.join(", ")} — not read yet, because no reader has seen that field before.
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {rows.map((f) => (
          <div key={f.key} data-testid={`${testid}-field-${k}-${f.key}`}
            className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 px-3 py-1.5">
            {/* ON A PHONE the label sits ABOVE the value. Side by side, a 12-character
                configuration number was breaking into four stacked fragments — unreadable, and a
                number you cannot read is a number you cannot check. */}
            <span className="text-[11px] text-slate-500 sm:w-[11.5rem] sm:shrink-0">{f.label}</span>
            <div className="flex items-center gap-2 flex-1 min-w-0">
            {f.blank ? (
              <span data-testid={`${testid}-blank-${k}-${f.key}`} className="text-[12px] italic text-slate-400 flex-1">Not on this plate</span>
            ) : (
              <span data-testid={`${testid}-value-${k}-${f.key}`} className="font-mono text-[12px] font-semibold text-slate-900 flex-1 break-words">{f.value}</span>
            )}
            {!f.blank && (f.verified_at ? (
              <span data-testid={`${testid}-verified-${k}-${f.key}`} title={`Checked by ${f.verified_by}`}
                className="inline-flex items-center gap-0.5 text-[10px] font-mono text-emerald-700 shrink-0">
                <Check className="w-3 h-3" />checked
              </span>
            ) : canVerify ? (
              <button data-testid={`${testid}-verify-${k}-${f.key}`} onClick={() => onVerify(plate.id, f.key)}
                className="inline-flex items-center gap-0.5 rounded-md border border-slate-300 px-1.5 h-6 text-[10px] font-mono text-slate-600 hover:border-emerald-500 hover:text-emerald-700 shrink-0">
                <Check className="w-3 h-3" />verify
              </button>
            ) : null)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
